"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

// In de admin-iframe of met ?liveEdit=1 doet dit component niets (zie hieronder).
function inEditModus(): boolean {
  return window !== window.parent || window.location.search.includes("liveEdit=1");
}

/**
 * Laag 3 van de live-bewerking: overal gemount (app/layout.tsx), haalt de
 * Firestore-collectie `siteTexts` op (via /api/site-texts) en herschrijft
 * de textContent van alle [data-t-key]-elementen, of — als er geen
 * [data-t-key]-match is — de .src van [data-i-key]-elementen (afbeeldingen)
 * die een override hebben. `siteTexts` is één ongedifferentieerde
 * {key, value}-collectie: hetzelfde document-ID kan dus zowel op een
 * tekst-attribuut als een afbeelding-attribuut slaan, maar nooit op beide
 * (sleutels zijn uniek en niet-overlappend tussen de twee categorieën).
 *
 * Doet NIETS als dit component zelf in de admin-iframe of ?liveEdit=1-modus
 * draait — anders vecht het met de contentEditable-/upload-staat van
 * VisualEditorBridge.tsx (Laag 2).
 *
 * Gebruikt bewust ALLEEN el.textContent / el.src (nooit innerHTML) — zie het
 * beveiligingsmodel: geen opgemaakte tekst binnen een live-bewerkt tekstblok,
 * een bewuste, veiligere keuze.
 *
 * De overrides worden NIET alleen bij de eerste snapshot toegepast. De server
 * rendert steeds de fallback-tekst, dus na elke client-navigatie (logo-klik van
 * /shop naar /) en bij elk laat gerenderd element (winkelmand die pas na het
 * laden van de wagen verschijnt, een lijst die na een fetch binnenkomt) staat er
 * weer de standaardtekst. Daarom bewaren we de actuele overrides in een map en
 * passen die opnieuw toe na (1) elke snapshot, (2) elke padwijziging en
 * (3) elke DOM-wijziging in <main> (MutationObserver, gedebounced).
 * Geen lus: de tweede pass schrijft alleen als de waarde echt afwijkt.
 */
export default function LiveTexts() {
  const pathname = usePathname();
  // key -> override-waarde (alleen niet-lege). Map i.p.v. object: een
  // documentnaam als "constructor" mag geen prototype-eigenschap raken.
  const overridesRef = useRef<Map<string, string>>(new Map());

  // Eén pass over alle bewerkbare elementen (één querySelectorAll, dan opzoeken
  // in de map) in plaats van een selector per sleutel: blijft goedkoop ook
  // bij een grote DOM (homepage ±600 KB) en een lange lijst overrides.
  const pasToe = useCallback(() => {
    const overrides = overridesRef.current;
    if (overrides.size === 0) return;
    document.querySelectorAll("[data-t-key],[data-i-key]").forEach((el) => {
      const tekstKey = el.getAttribute("data-t-key");
      if (tekstKey !== null) {
        const val = overrides.get(tekstKey);
        if (val !== undefined && el.textContent !== val) el.textContent = val;
        return;
      }
      // Geen tekst-element → afbeelding-override. Dit zet rechtstreeks de src
      // en omzeilt next/image se automatische responsive-srcset voor DEZE
      // afbeelding — een bewuste, geaccepteerde beperking: een via de
      // live-editor ingestelde vervangfoto is altijd één vaste URL
      // (rechtstreeks geüpload naar Storage). Staat er toch een srcset op het
      // element (bv. als de beeldoptimalisatie later wél werkt), dan wint die
      // van de src en zou de vervangfoto onzichtbaar blijven: die halen we weg.
      const beeldKey = el.getAttribute("data-i-key");
      const val = beeldKey !== null ? overrides.get(beeldKey) : undefined;
      if (val !== undefined && "src" in el && el.getAttribute("src") !== val) {
        el.removeAttribute("srcset");
        el.setAttribute("src", val);
      }
    });
  }, []);

  useEffect(() => {
    // De activatie-check staat BINNEN de effect-callback (niet als vroege
    // return vóór de hook) zodat de rules-of-hooks niet geschonden worden.
    if (inEditModus()) return;

    // Late renders: pas bij de eerste echte override aanzetten, zodat een
    // bezoeker zonder overrides er niets van merkt.
    let observer: MutationObserver | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startObserver = () => {
      if (observer) return;
      const root = document.getElementById("main-content") ?? document.body;
      observer = new MutationObserver(() => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          pasToe();
          // Onze eigen wijzigingen hierboven niet opnieuw als "nieuw" zien.
          observer?.takeRecords();
        }, 100);
      });
      observer.observe(root, { childList: true, subtree: true });
    };

    // MOW-PERF-OKT26: één keer ophalen via /api/site-texts in plaats van een onSnapshot-
    // abonnement op de collectie. Dat abonnement trok de hele Firestore-SDK (±470 KB geparseerd)
    // in de eerste bundel van elke pagina en hield een open verbinding vast, voor een handvol
    // korte teksten. Een bezoeker heeft geen baat bij live-updates; wijzigt de beheerder een
    // tekst, dan staat die bij de volgende paginalading op de site (in de live-editor zelf is
    // dit component uit, zie inEditModus). Zelfde regels als voorheen voor lege waarden.
    let afgebroken = false;
    (async () => {
      try {
        const res = await fetch("/api/site-texts");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Record<string, unknown>;
        if (afgebroken) return;
        const volgende = new Map<string, string>();
        for (const [sleutel, waarde] of Object.entries(data)) {
          const val = String(waarde || "").trim();
          // Lege/whitespace-only waarde = geen override → val terug naar de
          // statische fallback (die al op de pagina staat, dus niets doen).
          if (val) volgende.set(sleutel, val);
        }
        overridesRef.current = volgende;
        if (volgende.size > 0) startObserver();
        pasToe();
      } catch (err) {
        // Nooit laten crashen — als de server traag/onbereikbaar is blijft gewoon de
        // statische server-gerenderde fallback-tekst zichtbaar.
        console.error("[LiveTexts] ophalen van de overrides mislukt:", err);
      }
    })();

    return () => {
      afgebroken = true;
      observer?.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [pasToe]);

  // Client-navigatie: de nieuwe pagina is net met fallback-teksten gerenderd.
  // Twee frames wachten zodat React/Next de commit hebben afgerond; de
  // MutationObserver hierboven vangt alles wat daarna nog laat binnenkomt.
  // (Doet niets zolang er geen overrides zijn, dus ook niet in bewerk-modus.)
  useEffect(() => {
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(pasToe);
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [pathname, pasToe]);

  return null;
}
